import type { FlowEditorNode, FlowEditorState, FlowGridItem, WidgetAnchor } from "./types";
import { RGL_CONTAINER_PADDING } from "./constants";
import { getGridConfig, getGridRows } from "./flowViewportUtils";
import { clampGridItem, getGridItemPixels, getNodeGridItem, setNodeLayout } from "./flowGridUtils";
export const normalizeWidgetAnchor = (value: unknown): WidgetAnchor => {
  if (
    value === "center" ||
    value === "top" ||
    value === "bottom" ||
    value === "left" ||
    value === "right"
  ) {
    return value;
  }
  if (value === "bottom-left") return "bottom";
  return "free";
};

export const getNodeAnchor = (node: Pick<FlowEditorNode, "data">) =>
  normalizeWidgetAnchor(node.data.settings?.anchor);

export const isNodeAnchored = (node: FlowEditorNode) => getNodeAnchor(node) !== "free";

export const positionGridItemByAnchor = (
  item: FlowGridItem,
  viewport: FlowEditorState["viewport"],
  anchor: WidgetAnchor,
): FlowGridItem => {
  const nextItem = clampGridItem(item, viewport);
  const rows = getGridRows(viewport);
  const cols = getGridConfig(viewport.device).cols;
  const centeredX = Math.max(Math.floor((cols - nextItem.w) / 2), 0);
  const centeredY = Math.max(Math.floor((rows - nextItem.h) / 2), 0);

  if (anchor === "center") {
    return {
      ...nextItem,
      x: centeredX,
      y: centeredY,
    };
  }

  if (anchor === "top") {
    return {
      ...nextItem,
      x: centeredX,
      y: 0,
    };
  }

  if (anchor === "bottom") {
    return {
      ...nextItem,
      x: centeredX,
      y: Math.max(rows - nextItem.h, 0),
    };
  }

  if (anchor === "left") {
    return {
      ...nextItem,
      x: 0,
      y: centeredY,
    };
  }

  if (anchor === "right") {
    return {
      ...nextItem,
      x: Math.max(cols - nextItem.w, 0),
      y: centeredY,
    };
  }

  return nextItem;
};

export const getAnchoredGridItem = (
  node: FlowEditorNode,
  viewport: FlowEditorState["viewport"],
  anchor = getNodeAnchor(node),
) => {
  return positionGridItemByAnchor(
    getNodeGridItem(node, viewport),
    viewport,
    anchor,
  );
};

export const isGridItemBottomLeft = (
  item: FlowGridItem,
  viewport: FlowEditorState["viewport"],
) => {
  const rows = getGridRows(viewport);
  return item.x <= 0 && item.y >= Math.max(rows - item.h, 0);
};

export const getEffectiveNodeAnchor = (
  node: FlowEditorNode,
  viewport: FlowEditorState["viewport"],
): WidgetAnchor => {
  const anchor = getNodeAnchor(node);
  if (anchor !== "free") return anchor;

  return isGridItemBottomLeft(getNodeGridItem(node, viewport), viewport)
    ? "bottom"
    : "free";
};

export const recenterAnchoredNodes = (
  nodes: FlowEditorNode[],
  viewport: FlowEditorState["viewport"],
) =>
  nodes.map((node) => {
    const anchor = getNodeAnchor(node);
    return anchor === "free"
      ? node
      : setNodeLayout(
          node,
          viewport,
          getAnchoredGridItem(node, viewport, anchor),
        );
  });

export const setNodeLayoutByAnchor = (
  node: FlowEditorNode,
  viewport: FlowEditorState["viewport"],
  item: FlowGridItem,
  anchor = getNodeAnchor(node),
) =>
  setNodeLayout(
    node,
    viewport,
    positionGridItemByAnchor(item, viewport, anchor),
  );

export const getEditorNodePixels = (
  node: FlowEditorNode,
  viewport: FlowEditorState["viewport"],
  item: FlowGridItem,
) => {
  const pixels = getGridItemPixels(item, viewport);
  const anchor = getNodeAnchor(node);
  const width = pixels.width;
  const height = pixels.height;
  const centeredLeft = Math.max((viewport.width - width) / 2, 0);
  const centeredTop = Math.max((viewport.height - height) / 2, 0);

  if (anchor === "center") {
    return {
      ...pixels,
      left: centeredLeft,
      top: centeredTop,
    };
  }

  if (anchor === "top") {
    return {
      ...pixels,
      left: centeredLeft,
      top: RGL_CONTAINER_PADDING[1],
    };
  }

  if (anchor === "bottom") {
    return {
      ...pixels,
      left: centeredLeft,
      top: Math.max(viewport.height - height - RGL_CONTAINER_PADDING[1], 0),
    };
  }

  if (anchor === "left") {
    return {
      ...pixels,
      left: RGL_CONTAINER_PADDING[0],
      top: centeredTop,
    };
  }

  if (anchor === "right") {
    return {
      ...pixels,
      left: Math.max(viewport.width - width - RGL_CONTAINER_PADDING[0], 0),
      top: centeredTop,
    };
  }

  return pixels;
};
